/**
 * Join Request Pending — domain-match request-to-join confirmation.
 *
 * Shown after a domain-matched registrant requests to join an existing tenant
 * whose self-registration is OFF: the request is queued for a tenant admin to
 * approve or deny. The user stays tenant-less meanwhile, so we land them here
 * (with the option to cancel and create their own workspace instead) rather
 * than dropping them into an app.
 */

import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Clock, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../shared/auth';
import { STANDALONE_TITLE } from '../../shared/styles/typography';

interface JoinRequestPendingScreenProps {
  /** Name of the tenant the user asked to join. */
  tenantName: string;
  /** Email the request was raised with — echoed back as the notification target. */
  email: string;
  /** Cancel the pending request so the user can create their own workspace. */
  onCancel: () => Promise<void>;
}

export function JoinRequestPendingScreen({
  tenantName,
  email,
  onCancel,
}: JoinRequestPendingScreenProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isCancelling, setIsCancelling] = useState(false);

  const handleBackToSignIn = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      await onCancel();
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4 py-8">
      <Card className="w-full max-w-[600px] shadow-lg">
        <CardContent className="space-y-6 p-6 text-center sm:p-8">
          <div className="flex justify-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-warning/10 text-warning">
              <Clock className="h-8 w-8" />
            </span>
          </div>

          <div className="space-y-2">
            <h1 className={STANDALONE_TITLE}>
              {t('setupWizard.joinRequestPending.title')}
            </h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              {t('setupWizard.joinRequestPending.subtitle', { tenant: tenantName })}
            </p>
          </div>

          <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            <Trans
              t={t}
              i18nKey="setupWizard.joinRequestPending.notifyHint"
              values={{ email }}
              components={{ strong: <span className="font-medium text-foreground" /> }}
            />
          </div>

          <div className="space-y-3">
            <Button className="h-11 w-full" onClick={handleBackToSignIn} disabled={isCancelling}>
              {t('setupWizard.joinRequestPending.backToSignIn')}
            </Button>
            <Button
              variant="ghost"
              className="h-11 w-full text-muted-foreground"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('setupWizard.joinRequestPending.cancelAndCreate')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
